import type { BadgeVariant } from '../../components/ui/Badge';
import type { KhoaDich } from '../../i18n';
import type { KetLuan, KieuTieuChi, MucNghiemTrong, PhanQuyet, TrangThaiBo, TrangThaiKhoa, TrangThaiPhien } from './types';

/**
 * Bảng tông của module `tham-dinh` (ADR-0080). Nằm ở đây chứ không ở `src/mock/`: tầng mock sẽ bị xoá khi
 * API thật về, còn ý nghĩa màu là của phân hệ (CLAUDE.md).
 *
 * Chỉ dùng nhóm biến thể **trạng thái** của `Badge` — `success`/`warning`/`danger`/`primary`/`neutral`.
 * Nhóm phân loại (`violet`/`teal`/`orange`/`info`) cấm dùng lẫn vào đây: tô một kết luận màu `teal`
 * đọc thành "đây là loại teal", không phải "hồ sơ này đạt".
 */
type BienTheTrangThai = Extract<BadgeVariant, 'neutral' | 'primary' | 'success' | 'warning' | 'danger'>;

/**
 * Trạng thái một bộ tiêu chí.
 *
 * `hoat_dong` xanh: bộ dùng được để chấm. `luu_tru` xám: không phải sự cố, chỉ là không còn dùng —
 * đây cũng là chỗ backend mời chuyển tới khi không xoá được bộ đã phát hành.
 */
export const TRANG_THAI_BO: Record<TrangThaiBo, { khoa: KhoaDich; variant: BienTheTrangThai }> = {
  hoat_dong: { khoa: 'thamDinh.trangThaiBo.hoat_dong', variant: 'success' },
  luu_tru: { khoa: 'thamDinh.trangThaiBo.luu_tru', variant: 'neutral' },
};

/**
 * Vòng đời phiên thẩm định (đặc tả mục 4.4).
 *
 * Bốn trạng thái đầu là tiến trình đang chạy — xanh nhấn, không ai cần làm gì. `cho_duyet` và
 * `cho_phe_duyet` hổ phách vì **đang chờ một người**: đó là nơi hồ sơ đứng lại nếu không ai đụng vào.
 * `hoan_tat` xanh, `loi` đỏ.
 */
export const TRANG_THAI_PHIEN: Record<TrangThaiPhien, { khoa: KhoaDich; variant: BienTheTrangThai }> = {
  cho_xu_ly: { khoa: 'thamDinh.trangThaiPhien.cho_xu_ly', variant: 'primary' },
  dang_doc: { khoa: 'thamDinh.trangThaiPhien.dang_doc', variant: 'primary' },
  dang_cham: { khoa: 'thamDinh.trangThaiPhien.dang_cham', variant: 'primary' },
  cho_duyet: { khoa: 'thamDinh.trangThaiPhien.cho_duyet', variant: 'warning' },
  cho_phe_duyet: { khoa: 'thamDinh.trangThaiPhien.cho_phe_duyet', variant: 'warning' },
  hoan_tat: { khoa: 'thamDinh.trangThaiPhien.hoan_tat', variant: 'success' },
  loi: { khoa: 'thamDinh.trangThaiPhien.loi', variant: 'danger' },
};

/**
 * Kết luận của cả phiên, do code tính chứ không do mô hình (đặc tả mục 2.1).
 *
 * `chua_du_du_lieu` xám chứ không đỏ: thiếu bằng chứng **không phải** là trượt — đặc tả mục 2.3 tách hẳn
 * hai chuyện này, và tô đỏ ở đây đọc thành hồ sơ không đạt.
 */
export const KET_LUAN: Record<KetLuan, { khoa: KhoaDich; variant: BienTheTrangThai }> = {
  dat: { khoa: 'thamDinh.ketLuan.dat', variant: 'success' },
  dat_co_dieu_kien: { khoa: 'thamDinh.ketLuan.dat_co_dieu_kien', variant: 'warning' },
  khong_dat: { khoa: 'thamDinh.ketLuan.khong_dat', variant: 'danger' },
  chua_du_du_lieu: { khoa: 'thamDinh.ketLuan.chua_du_du_lieu', variant: 'neutral' },
};

/**
 * Phán quyết một tiêu chí (đặc tả mục 2.1).
 *
 * `khong_du_can_cu` xám chứ không đỏ: thiếu bằng chứng **không phải** là trượt — mục 2.3 tách hẳn hai chuyện
 * này, và tô đỏ ở đây đọc thành "tiêu chí này hỏng".
 */
export const PHAN_QUYET: Record<PhanQuyet, { khoa: KhoaDich; variant: BienTheTrangThai }> = {
  dat: { khoa: 'thamDinh.phanQuyet.dat', variant: 'success' },
  mot_phan: { khoa: 'thamDinh.phanQuyet.mot_phan', variant: 'warning' },
  khong_dat: { khoa: 'thamDinh.phanQuyet.khong_dat', variant: 'danger' },
  khong_ap_dung: { khoa: 'thamDinh.phanQuyet.khong_ap_dung', variant: 'neutral' },
  khong_du_can_cu: { khoa: 'thamDinh.phanQuyet.khong_du_can_cu', variant: 'neutral' },
};

/** Thứ tự trong ô chọn ghi đè: từ tốt nhất tới chưa kết luận được. */
export const THU_TU_PHAN_QUYET: readonly PhanQuyet[] = [
  'dat', 'mot_phan', 'khong_dat', 'khong_ap_dung', 'khong_du_can_cu',
];

/** Mức nghiêm trọng của một tiêu chí: thang tăng dần, nên màu cũng tăng dần. */
export const MUC_NGHIEM_TRONG: Record<MucNghiemTrong, { khoa: KhoaDich; variant: BienTheTrangThai }> = {
  thap: { khoa: 'thamDinh.mucNghiemTrong.thap', variant: 'neutral' },
  trung_binh: { khoa: 'thamDinh.mucNghiemTrong.trung_binh', variant: 'primary' },
  cao: { khoa: 'thamDinh.mucNghiemTrong.cao', variant: 'warning' },
  nghiem_trong: { khoa: 'thamDinh.mucNghiemTrong.nghiem_trong', variant: 'danger' },
};

export const TRANG_THAI_KHOA: Record<TrangThaiKhoa, { khoa: KhoaDich; variant: BienTheTrangThai }> = {
  hoat_dong: { khoa: 'thamDinh.trangThaiKhoa.hoat_dong', variant: 'success' },
  thu_hoi: { khoa: 'thamDinh.trangThaiKhoa.thu_hoi', variant: 'neutral' },
};

/**
 * Sáu kiểu tiêu chí (đặc tả mục 5.2). Không phải nhóm biến thể màu — đây là nhãn chọn trong trình soạn,
 * nên chỉ cần khoá dịch.
 */
export const KIEU_TIEU_CHI: Record<KieuTieuChi, KhoaDich> = {
  co_khong: 'thamDinh.kieu.co_khong',
  thang_diem: 'thamDinh.kieu.thang_diem',
  doi_chieu_gia_tri: 'thamDinh.kieu.doi_chieu_gia_tri',
  thanh_phan_ho_so: 'thamDinh.kieu.thanh_phan_ho_so',
  doi_chieu_cheo: 'thamDinh.kieu.doi_chieu_cheo',
  tu_do: 'thamDinh.kieu.tu_do',
};

/** Thứ tự hiện trong ô chọn: hai kiểu hay dùng nhất lên đầu, không theo thứ tự khai của đặc tả. */
export const THU_TU_KIEU: readonly KieuTieuChi[] = [
  'co_khong', 'thang_diem', 'doi_chieu_gia_tri', 'thanh_phan_ho_so', 'doi_chieu_cheo', 'tu_do',
];

/**
 * Mã quyền lớp 3, chép nguyên văn `@RequirePermission` của backend và `permissions` của `module.yaml`.
 * Màn hỏi bằng `useModulePermissions(QUYEN)`; thiếu quyền thì **ẩn** mục, không hiện mờ (ADR-UI-0005).
 */
export const QUYEN = {
  tongQuanXem: 'tham-dinh:tong-quan:read',
  danhMucXem: 'tham-dinh:danh-muc:read',
  danhMucTao: 'tham-dinh:danh-muc:create',
  danhMucSua: 'tham-dinh:danh-muc:update',
  danhMucXoa: 'tham-dinh:danh-muc:delete',
  boXem: 'tham-dinh:bo-tieu-chi:read',
  boTao: 'tham-dinh:bo-tieu-chi:create',
  boSua: 'tham-dinh:bo-tieu-chi:update',
  boXoa: 'tham-dinh:bo-tieu-chi:delete',
  phienXem: 'tham-dinh:phien:read',
  phienTao: 'tham-dinh:phien:create',
  phienSua: 'tham-dinh:phien:update',
  phienXoa: 'tham-dinh:phien:delete',
  phienXuat: 'tham-dinh:phien:export',
  pheDuyetXem: 'tham-dinh:phe-duyet:read',
  pheDuyetSua: 'tham-dinh:phe-duyet:update',
  /**
   * Hai quyền trợ lí tách nhau có chủ đích (đặc tả mục 14.2): `hoi` là ĐỌC (hỏi–đáp trên hồ sơ), còn
   * `viet-lai` là SỬA vì nó tiêu một lượt gọi mô hình.
   */
  troLyHoi: 'tham-dinh:tro-ly:hoi',
  troLyVietLai: 'tham-dinh:tro-ly:viet-lai',
} as const;

/** Số dòng mỗi trang; backend nhận `limit` 1–100. */
export const SO_DONG_TRANG = 20;
